import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customerAuthSession, customerMe, customerLogout } from "@/lib/api";

export const useCustomerAuth = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const processed = useRef(false);
    const [auth, setAuth] = useState(null); // { member, vouchers, picture }
    const [error, setError] = useState("");

    useEffect(() => {
        const m = (location.hash || "").match(/session_id=([^&]+)/);
        if (m) {
            if (processed.current) return;
            processed.current = true;
            customerAuthSession(decodeURIComponent(m[1]))
                .then(setAuth)
                .catch(() => setError("Google sign-in failed — please try again"))
                .finally(() => navigate(location.pathname + location.search, { replace: true }));
            return;
        }
        if (processed.current) return;
        processed.current = true;
        customerMe().then(setAuth).catch(() => setAuth(null));
    }, [location.hash, location.pathname, location.search, navigate]);

    const refresh = () => customerMe().then(setAuth).catch(() => setAuth(null));
    const signOut = async () => {
        await customerLogout();
        setAuth(null);
    };
    return { auth, error, signOut, refresh };
};

export const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const GoogleSignInButton = ({ compact, testid = "google-signin-btn" }) => (
    <button
        type="button"
        onClick={startGoogleSignIn}
        data-testid={testid}
        className={`inline-flex items-center justify-center gap-2 bg-white text-[#1f1f1f] font-medium transition-opacity hover:opacity-90 ${compact ? "text-[11px] px-2.5 py-1.5" : "w-full text-[14px] px-4 py-3"}`}
    >
        <svg width={compact ? 13 : 18} height={compact ? 13 : 18} viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
        </svg>
        {compact ? "Sign in" : "Continue with Google"}
    </button>
);

export const CustomerAuthBadge = ({ auth, onSignOut }) => (
    <div className="flex items-center gap-2" data-testid="customer-auth-badge">
        {auth.picture && <img src={auth.picture} alt="" className="w-6 h-6 rounded-full" />}
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold" data-testid="customer-auth-name">
            Hi {auth.member?.name?.split(" ")[0] || "club"}
        </span>
        <button onClick={onSignOut} className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted hover:text-[#ff5246]" data-testid="customer-signout-btn">
            Sign out
        </button>
    </div>
);

export const ClubVoucherChips = ({ vouchers, onApply, busy }) => {
    if (!vouchers?.length) return null;
    return (
        <div className="mb-4 space-y-2" data-testid="club-voucher-chips">
            {vouchers.map((v) => (
                <button
                    key={v.code}
                    type="button"
                    disabled={busy}
                    onClick={() => onApply(v.code)}
                    className="w-full flex items-center justify-between border border-dashed border-[var(--gold)] px-3 py-2.5 text-left hover:bg-[var(--surface-elevated)] transition-colors disabled:opacity-50"
                    data-testid={`club-voucher-apply-${v.code}`}
                >
                    <span>
                        <span className="block font-mono text-[12px] text-gold">{v.code}</span>
                        <span className="block font-mono text-[10px] text-muted uppercase tracking-[0.16em]">
                            Your Naami Club {v.kind === "bonus" ? "referral reward" : "voucher"} · one use
                        </span>
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Apply →</span>
                </button>
            ))}
        </div>
    );
};
